'use client';

import {
    CSSProperties,
    RefObject,
    useCallback,
    useEffect,
    useLayoutEffect,
    useState
} from 'react';

const VIEWPORT_MARGIN = 8;

interface AnchoredMenuOptions {
    /** Gap between the anchor and the menu. */
    gap?: number;
    /** Tallest the menu may get when there is room. */
    maxHeight?: number;
    /** Below this much room under the anchor, open upward if there's more room above. */
    minHeightBelow?: number;
    /** 'below' always opens downward (height fits the room below). */
    placement?: 'auto' | 'below';
    /** Narrowest the menu may be (it is otherwise as wide as the anchor). */
    minWidth?: number;
    /** Widest the menu may be. */
    maxWidth?: number;
}

/**
 * Fixed-position style for a dropdown rendered in a portal: anchored to
 * `anchorRef`, opening downward unless there's too little room below and
 * more above, sized to the room it gets and kept inside the viewport.
 * Follows the anchor on scroll (any scroll container) and resize while
 * `open`. Null until measured.
 */
export function useAnchoredMenu(
    anchorRef: RefObject<HTMLElement | null>,
    open: boolean,
    {
        gap = 6,
        maxHeight = 360,
        minHeightBelow = 260,
        placement = 'auto',
        minWidth = 280,
        maxWidth = 440
    }: AnchoredMenuOptions = {}
): CSSProperties | null {
    const [style, setStyle] = useState<CSSProperties | null>(null);

    const update = useCallback(() => {
        const anchor = anchorRef.current;
        if (!anchor) return;
        const rect = anchor.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        const roomBelow =
            viewportHeight - rect.bottom - gap - VIEWPORT_MARGIN;
        const roomAbove = rect.top - gap - VIEWPORT_MARGIN;
        const room = (up: boolean) => (up ? roomAbove : roomBelow);
        const openUp =
            placement === 'auto' &&
            roomBelow < minHeightBelow &&
            roomAbove > roomBelow;

        const width = Math.min(
            Math.max(rect.width, minWidth),
            maxWidth,
            viewportWidth - VIEWPORT_MARGIN * 2
        );
        const left = Math.min(
            Math.max(rect.left, VIEWPORT_MARGIN),
            viewportWidth - width - VIEWPORT_MARGIN
        );

        setStyle({
            position: 'fixed',
            left,
            width,
            maxHeight: Math.max(Math.min(maxHeight, room(openUp)), 120),
            ...(openUp
                ? { bottom: viewportHeight - rect.top + gap }
                : { top: rect.bottom + gap })
        });
    }, [
        anchorRef,
        gap,
        maxHeight,
        minHeightBelow,
        placement,
        minWidth,
        maxWidth
    ]);

    useLayoutEffect(() => {
        if (open) update();
        else setStyle(null);
    }, [open, update]);

    useEffect(() => {
        if (!open) return;
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [open, update]);

    return style;
}
